import React, { useEffect, useMemo, useState } from "react";
import {
  FileText, FilePdf, FileImage, FileDoc, FileZip, Folder,
  Eye, Download, CaretDown, CaretUp, House, CaretRight, ShareNetwork,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useNotifications } from "@/lib/notifications";
import { shareFile } from "@/lib/shareFile";

/** ==================== PropertyDocumentsSection ====================
 *
 * Sezione "Documenti Immobile" collassabile.
 *
 * Usata in:
 *  - KanbanBoard.jsx → all'interno di ogni card operatore
 *  - PracticeDetail.jsx → sezione dedicata nella pagina di dettaglio
 *
 * Regole di visibilità (governate dalla prop `canView`, decisa dal chiamante):
 *  - Sempre visibile ad admin/supervisor.
 *  - Visibile agli operatori assegnati (current + step_assignments successivi).
 *  - Nascosta quando la pratica è completata.
 *  - Nascosta al PRO owner (ha già accesso completo alla scheda immobile).
 *
 * Fetch lazy: la lista documenti viene richiesta solo al primo apri (evita
 * chiamate su tutte le card del Kanban).
 * Sola lettura: anteprima + download. Nessun upload/delete da qui.
 */
const IMAGE_EXTS = ["png", "jpg", "jpeg", "webp", "gif", "svg", "bmp"];
const isImageFile = (name = "") => IMAGE_EXTS.includes((name.split(".").pop() || "").toLowerCase());
const isPdfFile = (name = "") => (name.split(".").pop() || "").toLowerCase() === "pdf";
const getFileExt = (name = "") => (name.split(".").pop() || "").toLowerCase();

const DocIconSmall = ({ filename }) => {
  const ext = getFileExt(filename);
  if (isImageFile(filename)) return <FileImage size={16} color="#0891b2" weight="duotone" />;
  if (isPdfFile(filename)) return <FilePdf size={16} color="#dc2626" weight="duotone" />;
  if (["doc", "docx", "odt"].includes(ext)) return <FileDoc size={16} color="#2563eb" weight="duotone" />;
  if (["zip", "rar", "7z"].includes(ext)) return <FileZip size={16} color="#a16207" weight="duotone" />;
  return <FileText size={16} color="#4F46E5" weight="duotone" />;
};

/**
 * Props:
 *  - practiceId (string): id della pratica dalla quale risalire ai documenti dell'immobile.
 *  - canView (bool): flag di visibilità globale (calcolato dal chiamante).
 *  - variant ("compact" | "panel"): "compact" (default) è la variante Kanban
 *    con toggle e testo piccolo. "panel" è la variante Detail-page con
 *    heading più grande e padding maggiore.
 *  - defaultOpen (bool): se true, apri già espansa al primo render (utile
 *    nella page Detail dove non serve nascondere).
 */
export default function PropertyDocumentsSection({
  practiceId,
  canView,
  variant = "compact",
  defaultOpen = false,
}) {
  const [expanded, setExpanded] = useState(!!defaultOpen);
  const [loading, setLoading] = useState(false);
  const [docs, setDocs] = useState(null);
  const [folders, setFolders] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(null); // null = root
  const [error, setError] = useState("");
  // Iter93/Iter97: pallini rossi per notifiche non lette sulle cartelle di
  // servizio. Auto-mark al click sulla cartella (l'utente entra nella
  // cartella = destinazione finale della notifica di consegna).
  const { propertySummary, markRead } = useNotifications();
  const perFolder = propertySummary?.per_folder || {};

  // Auto-mark quando cambia la cartella corrente.
  useEffect(() => {
    if (!currentFolderId) return;
    if ((perFolder[currentFolderId] || 0) === 0) return;
    markRead({ folder_id: currentFolderId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFolderId]);

  const loadDocs = async () => {
    setLoading(true);
    setError("");
    try {
      const r = await api.get(`/practices/${practiceId}/property-documents`);
      setDocs(r.data?.documents || []);
      setFolders(r.data?.folders || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Errore nel caricamento documenti");
      setDocs([]);
      setFolders([]);
    } finally {
      setLoading(false);
    }
  };

  // Iter84: filtra cartelle/documenti per la cartella corrente (root = null)
  const currentFolders = useMemo(
    () => folders.filter(f => (f.parent_folder_id || null) === currentFolderId),
    [folders, currentFolderId],
  );
  const currentDocs = useMemo(
    () => (docs || []).filter(d => (d.folder_id || null) === currentFolderId),
    [docs, currentFolderId],
  );

  // Breadcrumb: risali la catena parent_folder_id
  const breadcrumb = useMemo(() => {
    if (!currentFolderId) return [];
    const path = [];
    let cur = currentFolderId;
    const guard = 20;
    let i = 0;
    while (cur && i < guard) {
      const f = folders.find(x => x._id === cur);
      if (!f) break;
      path.unshift(f);
      cur = f.parent_folder_id || null;
      i++;
    }
    return path;
  }, [folders, currentFolderId]);

  // Se defaultOpen è true, carica subito.
  React.useEffect(() => {
    if (defaultOpen && canView && docs === null) {
      loadDocs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultOpen, canView]);

  const toggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!expanded && docs === null) loadDocs();
    setExpanded(!expanded);
  };

  if (!canView) return null;

  const isPanel = variant === "panel";

  return (
    <div
      className={isPanel
        ? "kpi-card p-4"
        : "mt-2 border-t border-slate-100 pt-2"}
      onClick={(e) => e.stopPropagation()}
      data-testid={`property-docs-section-${practiceId}`}
    >
      <button
        type="button"
        onClick={toggle}
        className={isPanel
          ? "w-full flex items-center justify-between gap-3 text-left"
          : "w-full flex items-center justify-between text-[11px] font-semibold text-slate-600 hover:text-slate-900 py-1 px-1 rounded hover:bg-slate-50 transition-colors"}
        data-testid={`property-docs-toggle-${practiceId}`}
      >
        <span className={isPanel
          ? "flex items-center gap-2"
          : "flex items-center gap-1.5"}>
          <FileText
            size={isPanel ? 18 : 13}
            weight="duotone"
            className={isPanel ? "text-purple-600" : ""}
          />
          <span className={isPanel ? "font-display font-semibold text-slate-800" : ""}>
            Documenti Immobile
          </span>
          {docs && docs.length > 0 && (
            <span className={isPanel
              ? "ml-1 text-[11px] px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 font-mono"
              : "ml-1 text-[10px] px-1.5 rounded bg-slate-200 text-slate-700 font-mono"}>
              {docs.length}
            </span>
          )}
        </span>
        {expanded
          ? <CaretUp size={isPanel ? 16 : 12} className="text-slate-400" />
          : <CaretDown size={isPanel ? 16 : 12} className="text-slate-400" />}
      </button>
      {isPanel && (
        <p className="text-xs text-muted-foreground mt-1 mb-2">
          Documenti caricati sulla scheda immobile. Anteprima e download in sola lettura.
        </p>
      )}
      {expanded && (
        <div className={isPanel ? "mt-2 space-y-1" : "mt-1.5 space-y-1"} data-testid={`property-docs-list-${practiceId}`}>
          {loading && (
            <div className={isPanel
              ? "text-sm text-slate-400 italic px-1 py-2"
              : "text-[11px] text-slate-400 italic px-1 py-2"}>
              Caricamento…
            </div>
          )}
          {!loading && error && (
            <div className={isPanel
              ? "text-sm text-red-600 px-1 py-2"
              : "text-[11px] text-red-600 px-1 py-2"}>
              {error}
            </div>
          )}
          {!loading && !error && docs && (
            <>
              {/* Iter84: Breadcrumbs navigazione cartelle */}
              {folders.length > 0 && (
                <div
                  className={isPanel
                    ? "flex flex-wrap items-center gap-1 text-xs bg-slate-50 rounded px-2 py-1.5 mb-1"
                    : "flex flex-wrap items-center gap-0.5 text-[10px] bg-slate-50 rounded px-1.5 py-1 mb-1"}
                  data-testid={`property-docs-breadcrumb-${practiceId}`}
                >
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setCurrentFolderId(null); }}
                    className={`flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-purple-100 ${!currentFolderId ? "font-semibold text-purple-700" : "text-slate-600"}`}
                    data-testid={`property-docs-breadcrumb-root-${practiceId}`}
                  >
                    <House size={isPanel ? 12 : 10} weight="bold" />
                    <span>Radice</span>
                  </button>
                  {breadcrumb.map((f) => (
                    <React.Fragment key={f._id}>
                      <CaretRight size={isPanel ? 10 : 8} className="text-slate-400" />
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setCurrentFolderId(f._id); }}
                        className={`px-1.5 py-0.5 rounded hover:bg-purple-100 ${f._id === currentFolderId ? "font-semibold text-purple-700" : "text-slate-600"}`}
                        data-testid={`property-docs-breadcrumb-${f._id}`}
                      >
                        {f.name}
                      </button>
                    </React.Fragment>
                  ))}
                </div>
              )}

              {/* Cartelle della folder corrente */}
              {currentFolders.map((f) => {
                const nSub = folders.filter(x => x.parent_folder_id === f._id).length;
                const nDocs = (docs || []).filter(d => d.folder_id === f._id).length;
                const unread = perFolder[f._id] || 0;
                return (
                  <button
                    type="button"
                    key={f._id}
                    onClick={(e) => { e.stopPropagation(); setCurrentFolderId(f._id); }}
                    className={isPanel
                      ? `w-full flex items-center gap-2 py-1.5 px-2 rounded hover:bg-purple-50 group border border-slate-100 text-left ${unread > 0 ? "bg-red-50/50 border-red-200" : ""}`
                      : `w-full flex items-center gap-1.5 py-1 px-1.5 rounded hover:bg-purple-50 group text-left ${unread > 0 ? "bg-red-50/50" : ""}`}
                    data-testid={`property-folder-${practiceId}-${f._id}`}
                    title={unread > 0 ? "Clicca per aprire la cartella (rimuove il pallino)" : undefined}
                  >
                    <div className="relative shrink-0">
                      <Folder size={isPanel ? 16 : 14} weight="fill" color="#a855f7" />
                      {unread > 0 && (
                        <span
                          className="absolute -top-1 -right-1 min-w-[14px] h-[14px] px-1 rounded-full bg-red-500 text-white text-[8px] font-bold flex items-center justify-center shadow ring-1 ring-white"
                          data-testid={`property-folder-badge-${f._id}`}
                        >
                          {unread > 9 ? "9+" : unread}
                        </span>
                      )}
                    </div>
                    <span className={isPanel
                      ? "flex-1 min-w-0 text-sm font-medium text-slate-800 truncate"
                      : "flex-1 min-w-0 text-[11px] font-medium text-slate-800 truncate"}>
                      {f.name}
                    </span>
                    <span className={isPanel
                      ? "text-[10px] text-slate-500 font-mono"
                      : "text-[9px] text-slate-500 font-mono"}>
                      {nSub > 0 ? `${nSub} sub · ` : ""}{nDocs} file
                    </span>
                    <CaretRight size={isPanel ? 14 : 11} className="text-slate-300" />
                  </button>
                );
              })}

              {/* Nessun contenuto nella cartella corrente */}
              {currentFolders.length === 0 && currentDocs.length === 0 && (
                <div className={isPanel
                  ? "text-sm text-slate-400 italic px-1 py-2"
                  : "text-[11px] text-slate-400 italic px-1 py-2"}>
                  {currentFolderId ? "Cartella vuota." : "Nessun documento caricato dall'immobile."}
                </div>
              )}

              {/* Documenti della folder corrente */}
              {currentDocs.map((d) => {
                const name = d.file_name || d.filename || d.name || "—";
                const url = d.signed_url || d.file_url;
                return (
                  <div
                    key={d._id || d.id}
                    className={isPanel
                      ? "flex items-center gap-2 py-1.5 px-2 rounded hover:bg-slate-50 group border border-slate-100"
                      : "flex items-center gap-1.5 py-1 px-1.5 rounded hover:bg-slate-50 group"}
                    data-testid={`property-doc-${practiceId}-${d._id || d.id}`}
                  >
                    <DocIconSmall filename={name} />
                    <span
                      className={isPanel
                        ? "flex-1 min-w-0 text-sm text-slate-700 truncate"
                        : "flex-1 min-w-0 text-[11px] text-slate-700 truncate"}
                      title={name}
                    >
                      {name}
                    </span>
                    {url && (
                      <>
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className={isPanel
                            ? "p-1.5 rounded hover:bg-purple-100 text-purple-700"
                            : "p-1 rounded hover:bg-purple-100 text-purple-700"}
                          title="Apri anteprima"
                          data-testid={`property-doc-preview-${d._id || d.id}`}
                        >
                          <Eye size={isPanel ? 15 : 13} weight="bold" />
                        </a>
                        <a
                          href={url}
                          download={name}
                          onClick={(e) => e.stopPropagation()}
                          className={isPanel
                            ? "p-1.5 rounded hover:bg-emerald-100 text-emerald-700"
                            : "p-1 rounded hover:bg-emerald-100 text-emerald-700"}
                          title="Scarica"
                          data-testid={`property-doc-download-${d._id || d.id}`}
                        >
                          <Download size={isPanel ? 15 : 13} weight="bold" />
                        </a>
                        {/* Iter140l: condivisione nativa mobile con signed URL fresco */}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); shareFile({ url, filename: name, title: name }); }}
                          className={isPanel
                            ? "p-1.5 rounded hover:bg-sky-100 text-sky-700"
                            : "p-1 rounded hover:bg-sky-100 text-sky-700"}
                          title="Condividi (WhatsApp, AirDrop, …)"
                          data-testid={`property-doc-share-${d._id || d.id}`}
                        >
                          <ShareNetwork size={isPanel ? 15 : 13} weight="bold" />
                        </button>
                      </>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}
